import { useEffect, useRef } from "react";

/**
 * Plays distinct chimes + vibration + browser notification at KEY THRESHOLDS:
 *   position 3 → 2 → 1 → 0 (= next) → called
 *
 * Guarantees:
 *   - Each threshold fires EXACTLY ONCE per active ticket (no duplicates)
 *   - Reset when a new ticket starts (different id or status returns to waiting from non-active)
 *   - Silent otherwise (no noise on every poll)
 *
 * This runs fully in the browser — requires the tab to be alive (foreground, background tab,
 * or screen-locked). It does NOT cover "app completely closed" — that needs Web Push + SW.
 */

const FREQ = {
  t5: { freqs: [523, 659], type: "sine" },          // discrete heads-up (5 left)
  t4: { freqs: [587, 698], type: "sine" },          // a little higher (4 left)
  t3: { freqs: [660, 880], type: "sine" },          // gentle rising
  t2: { freqs: [740, 988], type: "sine" },          // slightly higher
  t1: { freqs: [880, 1175, 1318], type: "sine" },   // triple alert
  t0: { freqs: [988, 1318, 988, 1318], type: "sine" }, // urgent
  called: { freqs: [880, 1318, 1760], type: "sine" },  // triumphant
};

const TITLE = {
  t5: "Votre tour approche",
  t4: "Préparez-vous",
  t3: "Votre passage se rapproche",
  t2: "Votre tour arrive bientôt",
  t1: "Préparez-vous, vous passez juste après",
  t0: "Vous êtes le prochain",
  called: "C'est votre tour",
};

const BODY = {
  t5: "Plus que 5 personnes avant vous.",
  t4: "Plus que 4 personnes avant vous.",
  t3: "Encore 3 personnes avant vous.",
  t2: "Plus que 2 personnes avant vous.",
  t1: "Vous passez juste après la personne en cours.",
  t0: "Vous êtes le prochain à être appelé.",
  called: "Veuillez vous présenter à l'accueil.",
};

const VIBRATE = {
  t5: [80, 60, 80],
  t4: [100, 70, 100],
  t3: [120, 80, 120],
  t2: [150, 80, 150, 80, 150],
  t1: [200, 100, 200, 100, 200],
  t0: [220, 100, 220, 100, 220, 100, 220],
  called: [250, 120, 250, 120, 400],
};

function playChime(pattern) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    pattern.freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = pattern.type;
      const t0 = i * 0.18;
      osc.frequency.setValueAtTime(f, now + t0);
      g.gain.setValueAtTime(0, now + t0);
      g.gain.linearRampToValueAtTime(0.32, now + t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + t0 + 0.22);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + t0);
      osc.stop(now + t0 + 0.22);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch {}
}

function vibrate(pattern) {
  try { navigator.vibrate && navigator.vibrate(pattern); } catch {}
}

function notify(title, body) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    new Notification(title, {
      body,
      tag: `bpg-threshold-${Date.now()}`, // allow multiple to stack briefly
      icon: "/favicon.ico",
      badge: "/favicon.ico",
    });
  } catch {}
}

function fireAlert(key, ticket) {
  const pattern = FREQ[key];
  if (!pattern) return;
  const title = TITLE[key];
  const baseBody = BODY[key] || "";
  const placeRef = ticket?.commerceName
    ? `${ticket.commerceName}${ticket?.queueName ? " · file " + ticket.queueName : ""}`
    : "";
  const body = placeRef ? `${baseBody}\n${placeRef}` : baseBody;
  playChime(pattern);
  vibrate(VIBRATE[key]);
  notify(title, body);
}

/**
 * Hook: watch the ticket and trigger an alert when each threshold is crossed.
 * Exposes a `flash` value that the UI can use to briefly highlight the ticket card.
 */
export function useTicketThresholdAlerts(ticket, setFlash) {
  const firedRef = useRef({}); // per ticketId: Set of keys already fired
  const lastIdRef = useRef(null);

  useEffect(() => {
    if (!ticket) {
      firedRef.current = {};
      lastIdRef.current = null;
      return;
    }
    // Reset on new ticket
    if (ticket.id !== lastIdRef.current) {
      firedRef.current[ticket.id] = new Set();
      lastIdRef.current = ticket.id;
    }
    const fired = firedRef.current[ticket.id] || new Set();

    const shouldFire = (key) => !fired.has(key);
    const mark = (key) => { fired.add(key); firedRef.current[ticket.id] = fired; };

    // Called takes precedence
    if (ticket.status === "called") {
      if (shouldFire("called")) {
        fireAlert("called", ticket);
        mark("called");
        setFlash && setFlash("called");
      }
      return;
    }

    if (ticket.status !== "waiting") return;
    const pos = ticket.position;

    // Fire in order: skip higher thresholds if already passed them
    if (pos <= 0 && shouldFire("t0")) {
      fireAlert("t0", ticket); mark("t0"); setFlash && setFlash("t0");
    } else if (pos === 1 && shouldFire("t1")) {
      fireAlert("t1", ticket); mark("t1"); setFlash && setFlash("t1");
    } else if (pos === 2 && shouldFire("t2")) {
      fireAlert("t2", ticket); mark("t2"); setFlash && setFlash("t2");
    } else if (pos === 3 && shouldFire("t3")) {
      fireAlert("t3", ticket); mark("t3"); setFlash && setFlash("t3");
    } else if (pos === 4 && shouldFire("t4")) {
      fireAlert("t4", ticket); mark("t4"); setFlash && setFlash("t4");
    } else if (pos === 5 && shouldFire("t5")) {
      fireAlert("t5", ticket); mark("t5"); setFlash && setFlash("t5");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, ticket?.status, ticket?.position]);
}
